import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as Copy02Icon } from "components/Icons/copy-02.svg";
import { ReactComponent as Pencil02Icon } from "components/Icons/pencil-02.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import {
  CampaignConditionNewFormat,
  TransactionItem,
  TransactionItemFilterType,
} from "types/campaigns";
import { ConditionError } from "./Condition";
import { Condition, ConditionReadOnly } from "./ConditionReadOnly";
import { DotMenu } from "./DotMenu";
import { NewFormatConditionReadOnly } from "./NewFormatConditions/NewFormatConditionReadOnly";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    iconsWrapper: {
      display: "flex",
      alignItems: "center",
    },
    iconBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "50%",
      width: theme.spacing(12),
      minWidth: theme.spacing(12),
      height: theme.spacing(12),
      background: theme.gray50,
      margin: theme.spacing(0, 2),
      cursor: "pointer",
    },
    conditionWrapper: {
      display: "flex",
      justifyContent: "space-between",
      padding: theme.spacing(6, 8),
    },
    condition: {
      display: "flex",
      alignItems: "center",
      maxWidth: "80%",
    },
    icon: {
      width: theme.spacing(8),
      height: theme.spacing(8),
      color: theme.gray500,
    },
    error: {
      color: theme.red500,
      margin: theme.spacing(0, 2),
    },
  }),
);

interface Props {
  remove: () => void;
  edit: () => void;
  duplicate: () => void;
  condition: TransactionItemFilterType | CampaignConditionNewFormat | Condition;
  index: number;
  error?: ConditionError;
  isRefferalCampaign: boolean;
  "data-test-id": string;
  isAchievementForm?: boolean;
  transactionItemsFilters?: TransactionItem[];
  dotsMenu?: boolean;
}
export const ConditionReadOnlyWrapper = ({
  remove,
  edit,
  duplicate,
  condition,
  index,
  error,
  isRefferalCampaign,
  "data-test-id": dataTestId,
  isAchievementForm,
  transactionItemsFilters,
  dotsMenu,
}: Props) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles({ isAchievementForm });
  const { message } = useMessage();
  const isError = !!error;
  const isNewFormat = "value" in condition;

  return (
    <div className={classes.conditionWrapper}>
      <div className={classes.condition}>
        <span>{index + 1}.</span>
        {isNewFormat ? (
          <NewFormatConditionReadOnly
            conditionObject={condition}
            transactionItemsFilters={transactionItemsFilters}
            data-test-id={`${dataTestId}-condition`}
            error={error}
          />
        ) : (
          <ConditionReadOnly
            condition={condition}
            isRefferalCampaign={isRefferalCampaign}
            data-test-id={`${dataTestId}-condition`}
            error={error}
          />
        )}
      </div>
      <div className={classes.iconsWrapper}>
        {isError && (
          <AlertTriangleIcon
            className={classes.error}
            data-test-id={`${dataTestId}-error`}
          />
        )}
        {dotsMenu ? (
          <DotMenu
            actions={[
              {
                id: "edit",
                label: message({ id: "edit" }),
                onClick: edit,
              },
              {
                id: "duplicate",
                label: message({ id: "duplicate" }),
                onClick: duplicate,
              },
              {
                id: "delete",
                label: message({ id: "delete" }),
                onClick: () => setOpen(true),
              },
            ]}
            data-test-id={dataTestId}
          />
        ) : (
          <>
            <div className={classes.iconBox}>
              <Pencil02Icon
                onClick={edit}
                className={classes.icon}
                data-test-id={`${dataTestId}-edit`}
              />
            </div>

            <div className={classes.iconBox}>
              <Copy02Icon
                onClick={duplicate}
                className={classes.icon}
                data-test-id={`${dataTestId}-duplicate`}
              />
            </div>

            <div className={classes.iconBox}>
              <XCloseIcon
                onClick={() => setOpen(true)}
                className={classes.icon}
                data-test-id={`${dataTestId}-delete`}
              />
            </div>
          </>
        )}
        <ConfirmationModal
          action={() => {
            setOpen(false);
            remove();
          }}
          close={() => {
            setOpen(false);
          }}
          cancelText={message({ id: "cancel" })}
          header="deleteCondition"
          info="deleteConditionConfirm"
          open={open}
          submitText="deleteCondition"
        />
      </div>
    </div>
  );
};
